import axios, { AxiosInstance } from 'axios';
import { jwtDecode } from 'jwt-decode';
import type {
  AdminUser,
  Organization,
  AuthTokens,
  Room,
  Connection,
  GlossaryTerm,
  ErrorLog,
  SystemHealth,
  RoomMetrics,
  RecognitionCandidate,
  TeamMember,
  UserInvitation,
  PaginatedUsers,
  UserRole,
  UserStatus,
  OrganizationDetails,
  UsageStats,
  Subscription,
} from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class AdminClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: `${API_URL}`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = this.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = this.getRefreshToken();
            if (refreshToken) {
              const { data } = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
                refreshToken,
              });
              this.setTokens(data.data.tokens);
              originalRequest.headers.Authorization = `Bearer ${data.data.tokens.accessToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            this.clearTokens();
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(email: string, password: string): Promise<{ user: AdminUser; organization: Organization; tokens: AuthTokens }> {
    const { data } = await axios.post(`${API_URL}/api/v1/auth/login`, {
      email,
      password,
    });
    this.setTokens(data.data.tokens);
    return data.data;
  }

  async signup(
    organizationName: string,
    ownerEmail: string,
    ownerPassword: string,
    ownerDisplayName?: string
  ): Promise<{ user: AdminUser; organization: Organization; tokens: AuthTokens }> {
    const { data } = await axios.post(`${API_URL}/api/v1/auth/signup`, {
      organizationName,
      ownerEmail,
      ownerPassword,
      ownerDisplayName,
    });
    this.setTokens(data.data.tokens);
    return data.data;
  }

  async refreshToken(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) throw new Error('No refresh token');

    const { data } = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
      refreshToken,
    });
    this.setTokens(data.data.tokens);
  }

  async logout(): Promise<void> {
    // JWT is stateless, so we only need to clear client-side tokens
    this.clearTokens();
  }

  async getCurrentUser(): Promise<{ user: AdminUser; organization: Organization }> {
    const { data } = await axios.get(`${API_URL}/api/v1/auth/me`, {
      headers: {
        Authorization: `Bearer ${this.getAccessToken()}`,
      },
    });
    return data.data;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const { data } = await axios.post(`${API_URL}/api/v1/auth/forgot-password`, { email });
    return data;
  }

  async resetPassword(email: string, token: string, newPassword: string): Promise<{ message: string }> {
    const { data } = await axios.post(`${API_URL}/api/v1/auth/reset-password`, { email, token, newPassword });
    return data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const { data } = await this.client.put('/api/v1/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return data;
  }

  // Token management
  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  private setTokens(tokens: AuthTokens): void {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    if (tokens.expiresIn) {
      localStorage.setItem('tokenExpiresIn', tokens.expiresIn.toString());
    }
  }

  getStoredTokens(): AuthTokens | null {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    const expiresIn = localStorage.getItem('tokenExpiresIn');

    if (!accessToken || !refreshToken) return null;

    return {
      accessToken,
      refreshToken,
      expiresIn: expiresIn ? parseInt(expiresIn, 10) : undefined,
    };
  }

  private clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiresIn');
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      const decoded: any = jwtDecode(token);
      return decoded.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  // Rooms
  async getRooms(): Promise<Room[]> {
    const { data } = await this.client.get('/api/rooms');
    return data.rooms || [];
  }

  async createRoom(room: { id: string; name: string; description: string }): Promise<Room> {
    const { data } = await this.client.post('/admin/rooms', room);
    return data.room;
  }

  async updateRoom(roomId: string, updates: Partial<Room>): Promise<Room> {
    const { data } = await this.client.put(`/admin/rooms/${roomId}`, updates);
    return data.room;
  }

  async deleteRoom(roomId: string): Promise<void> {
    await this.client.delete(`/admin/rooms/${roomId}`);
  }

  async toggleRoom(roomId: string, enabled: boolean): Promise<Room> {
    const { data } = await this.client.put(`/admin/rooms/${roomId}`, { isActive: enabled });
    return data.room;
  }

  // Room Phrases (for speech recognition accuracy)
  async getRoomPhrases(roomId: string): Promise<string[]> {
    const { data } = await this.client.get(`/admin/rooms/${roomId}/phrases`);
    return data.phrases || [];
  }

  async updateRoomPhrases(roomId: string, phrases: string[]): Promise<string[]> {
    const { data } = await this.client.put(`/admin/rooms/${roomId}/phrases`, { phrases });
    return data.phrases || [];
  }

  // Recognition Candidates (Quality Monitoring)
  async getCandidates(
    roomId: string,
    params?: {
      status?: 'pending' | 'reviewed' | 'added' | 'dismissed';
      source?: 'auto' | 'admin' | 'mobile';
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    candidates: RecognitionCandidate[];
    total: number;
    pending: number;
    reviewed: number;
    added: number;
    dismissed: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.set('status', params.status);
    if (params?.source) queryParams.set('source', params.source);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());

    const { data } = await this.client.get(
      `/admin/rooms/${roomId}/candidates?${queryParams.toString()}`
    );
    return data;
  }

  async getCandidate(roomId: string, candidateId: string): Promise<RecognitionCandidate> {
    const { data } = await this.client.get(`/admin/rooms/${roomId}/candidates/${candidateId}`);
    return data.candidate;
  }

  async reviewCandidate(
    roomId: string,
    candidateId: string,
    review: {
      status: 'reviewed' | 'added' | 'dismissed';
      suggestedText?: string;
      reviewComment?: string;
    }
  ): Promise<RecognitionCandidate> {
    const { data } = await this.client.post(
      `/admin/rooms/${roomId}/candidates/${candidateId}/review`,
      review
    );
    return data.candidate;
  }

  async batchAddToPhraseList(
    roomId: string,
    candidateIds: string[],
    options: { addOriginal: boolean; addSuggested: boolean }
  ): Promise<{ addedPhrases: string[]; candidatesUpdated: number }> {
    const { data } = await this.client.post(`/admin/rooms/${roomId}/candidates/batch-add`, {
      candidateIds,
      ...options,
    });
    return data;
  }

  async flagCandidateAsAdmin(
    roomId: string,
    text: string,
    suggestedText?: string
  ): Promise<RecognitionCandidate> {
    const { data } = await this.client.post(`/admin/rooms/${roomId}/candidates/flag`, {
      text,
      suggestedText,
    });
    return data.candidate;
  }

  // Connections
  async getConnections(_roomId?: string): Promise<Connection[]> {
    // API returns summary data, not individual connections
    // Individual connection tracking is not implemented in backend
    return [];
  }

  async disconnectConnection(_connectionId: string): Promise<void> {
    // Not implemented in backend yet
    console.warn('disconnectConnection not implemented');
  }

  // Room Metrics
  async getRoomMetrics(_roomId?: string): Promise<RoomMetrics[]> {
    const { data } = await this.client.get('/admin/rooms/status');
    // Transform API response to RoomMetrics format
    return (data.rooms || []).map((room: any) => ({
      roomId: room.roomId,
      totalConnections: room.activeConnections || 0,
      connectionsByLanguage: room.connectionsByLanguage || {},
      averageLatency: Math.round((room.translationMetrics?.averageLatency || 0) * 1000), // Convert to ms
      errorRate: room.translationMetrics?.errorRate || 0,
      uptime: 0, // Not provided by API
    }));
  }

  // Glossary
  async getGlossary(): Promise<GlossaryTerm[]> {
    // Not implemented in backend yet
    return [];
  }

  async createGlossaryTerm(_term: Omit<GlossaryTerm, 'id' | 'createdAt' | 'updatedAt'>): Promise<GlossaryTerm> {
    // Not implemented in backend yet
    throw new Error('Not implemented');
  }

  async updateGlossaryTerm(_id: string, _updates: Partial<GlossaryTerm>): Promise<GlossaryTerm> {
    // Not implemented in backend yet
    throw new Error('Not implemented');
  }

  async deleteGlossaryTerm(_id: string): Promise<void> {
    // Not implemented in backend yet
    throw new Error('Not implemented');
  }

  async uploadGlossary(_file: File): Promise<{ imported: number; errors: string[] }> {
    // Not implemented in backend yet
    throw new Error('Not implemented');
  }

  async exportGlossary(): Promise<Blob> {
    // Not implemented in backend yet
    throw new Error('Not implemented');
  }

  // Language Updates
  async checkLanguageUpdates(): Promise<any> {
    const { data } = await this.client.get('/admin/languages/updates');
    return data;
  }

  // System Health
  async getSystemHealth(): Promise<SystemHealth> {
    try {
      const { data } = await this.client.get('/health', {
        validateStatus: (status) => status === 200 || status === 503, // Accept both 200 and 503
      });
      return {
        timestamp: data.timestamp,
        services: {
          database: data.status === 'healthy' ? 'operational' : 'degraded',
          cache: data.cache?.healthy ? 'operational' : 'degraded',
          translation: 'operational',
          tts: 'operational',
        },
        metrics: {
          uptime: 0,
          requestsPerMinute: 0,
          activeConnections: 0,
          cacheHitRate: 0,
        },
      };
    } catch (error) {
      // Return degraded status if health check fails
      return {
        timestamp: new Date().toISOString(),
        services: {
          database: 'down',
          cache: 'down',
          translation: 'down',
          tts: 'down',
        },
        metrics: {
          uptime: 0,
          requestsPerMinute: 0,
          activeConnections: 0,
          cacheHitRate: 0,
        },
      };
    }
  }

  // Error Logs
  async getErrorLogs(_limit = 100): Promise<ErrorLog[]> {
    // Not implemented in backend yet
    return [];
  }

  async clearErrorLogs(): Promise<void> {
    // Not implemented in backend yet
    console.warn('clearErrorLogs not implemented');
  }

  // Team Management
  async listUsers(page = 1, pageSize = 20): Promise<PaginatedUsers> {
    const { data } = await this.client.get(`/api/v1/users?page=${page}&pageSize=${pageSize}`);
    return {
      users: data.data.users || [],
      total: data.data.total || 0,
      page: data.data.page || page,
      pageSize: data.data.pageSize || pageSize,
      totalPages: data.data.totalPages || 1,
    };
  }

  async inviteUser(email: string, role: UserRole): Promise<UserInvitation> {
    const { data } = await this.client.post('/api/v1/users/invite', { email, role });
    return data.data.invitation;
  }

  async listInvitations(): Promise<UserInvitation[]> {
    const { data } = await this.client.get('/api/v1/users/invitations');
    return data.data.invitations || [];
  }

  async cancelInvitation(invitationId: string): Promise<void> {
    await this.client.delete(`/api/v1/invitations/${invitationId}`);
  }

  async updateUserRole(userId: string, role: UserRole): Promise<TeamMember> {
    const { data } = await this.client.put(`/api/v1/users/${userId}/role`, { role });
    return data.data.user;
  }

  async updateUserStatus(userId: string, status: UserStatus): Promise<TeamMember> {
    const { data } = await this.client.put(`/api/v1/users/${userId}/status`, { status });
    return data.data.user;
  }

  async deleteUser(userId: string): Promise<void> {
    await this.client.delete(`/api/v1/users/${userId}`);
  }

  async updateUserCredentials(
    userId: string,
    data: { email?: string; password?: string; displayName?: string }
  ): Promise<TeamMember> {
    const { data: response } = await this.client.put(`/api/v1/users/${userId}/credentials`, data);
    return response.data.user;
  }

  // Organization Management
  async getOrganization(): Promise<OrganizationDetails> {
    const { data } = await this.client.get('/api/v1/organization');
    return data.data.organization;
  }

  async updateOrganization(updates: { name?: string; logoUrl?: string }): Promise<OrganizationDetails> {
    const { data } = await this.client.put('/api/v1/organization', updates);
    return data.data.organization;
  }

  async getUsageStats(): Promise<UsageStats> {
    const { data } = await this.client.get('/api/v1/organization/usage');
    return data.data;
  }

  async getSubscription(): Promise<Subscription> {
    const { data } = await this.client.get('/api/v1/billing/subscription');
    return data.data.subscription;
  }
}

export const adminClient = new AdminClient();
export default adminClient;
